import * as React from 'react';
import { createFileRoute, useBlocker } from '@tanstack/react-router';
import { useState } from 'react';
import { ShopProfile } from '@next-is-free-monorepo/next-is-free-types';
import {
  Button,
  Form,
  Input,
  InputNumber,
  message, Select
} from 'antd';
import {
  ArrowRightOutlined,
  MinusCircleOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { supabase } from '../../../../utils/supabase';
import { compressImage } from '../../../../utils/imageCompression';
import useShopAttrs from '../../../../utils/hooks/useShopAttrs';
import ImageUploadInput from '../../../../components/ImageUploadInput';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { profileQueryOptions } from '../../../../utils/queryOptions';
import { apiV1 } from '../../../../utils/apiClient';
import posthog from 'posthog-js';

export const Route = createFileRoute('/_authenticated/customize/')({
  component: RouteComponent
});

function RouteComponent() {
  const [image, setImage] = useState<File | undefined>(undefined);
  const [colorPickerError, setColorPickerError] = useState(false);
  const [touched, setTouched] = useState(false);

  const [profileForm] = Form.useForm();

  const queryClient = useQueryClient();

  const shopAttrs = useShopAttrs();
  const shopProfile = useSuspenseQuery(profileQueryOptions());

  useBlocker({
    disabled: !touched,
    shouldBlockFn: () => {
      if (!touched) return false;
      const shouldLeave = confirm('Vuoi lasciare la pagina? le modifiche non salvate andranno perse');
      if (shouldLeave) {
        setTouched(false);
        queryClient.invalidateQueries({ queryKey: ['profile'] });
        profileForm.resetFields();
      }
      return !shouldLeave;
    }
  });

  const saveProfile = useMutation({
    mutationFn: async (variables: ShopProfile) => {
      if (!shopProfile.data.logo_preview_url) {
        throw new Error('Carica un\'immagine');
      }
      if (colorPickerError) {
        throw new Error('Seleziona un colore valido');
      }
      //upload new logo
      if (image) {
        const compressedImage = await compressImage(image);
        const { data: imageResponse, error: uploadError } =
          await supabase.storage
            .from('main')
            .upload(shopAttrs.data.id + '/logo.png', compressedImage, {
              upsert: true
            });
        if (uploadError) {
          throw uploadError;
        }
      }

      const sortedGoals = variables.goals.sort((a, b) => a.number - b.number);

      await apiV1.put('/shop/profile', { ...variables, goals: sortedGoals });
    },
    onError: (error) => {
      if (error.message) {
        message.error(error.message);
      }
    },
    onSuccess: async () => {
      setTouched(false);
      posthog.capture('profile_customization_saved', { business_name: shopAttrs.data.business_name });
      await shopAttrs.invalidate();
      message.success('Le modifiche sono state pubblicate');
    }
  });

  const handleFormChange = (_: any, values: ShopProfile) => {
    setTouched(true);
    queryClient.setQueryData(['profile'], () => {
      return values;
    });
  };

  const setBrandColor = (color: string) => {
    profileForm.setFieldValue('brand_color', color);
    setTouched(true);
    queryClient.setQueryData(['profile'], (oldData: any) => {
      return { ...oldData, brand_color: color };
    });
  };

  const setLogoPreviewUrl = (url: string) => {
    profileForm.setFieldValue('logo_preview_url', url);
    setTouched(true);
    queryClient.setQueryData(['profile'], (oldData: any) => {
      return { ...oldData, logo_preview_url: url };
    });
  };


  return (
    <Form
      form={profileForm}
      layout="vertical"
      onFinish={saveProfile.mutate}
      onFinishFailed={() => message.error('Completa tutti i campi')}
      onValuesChange={handleFormChange}
      initialValues={{ ...shopProfile.data }}
    >
      <Form.Item className={'hidden'} name={'logo_preview_url'} />
      <Form.Item className={'px-2 py-5'} rules={[{ required: true }]} name={'brand_color'} label={'logo'}>
        <ImageUploadInput
          objectFit={'cover'}
          setImagePreviewUrl={setLogoPreviewUrl}
          imagePreviewUrl={shopProfile.data.logo_preview_url}
          image={image}
          setImage={(props: File) => {
            setImage(props);
            setTouched(true);
          }}
          setColor={setBrandColor}
          color={shopProfile.data.brand_color}
          colorPickerError={colorPickerError}
          setColorPickerError={setColorPickerError}
        />
      </Form.Item>
      <Form.Item className={'p-3 border-t'} label={'Bio'} name={'bio'}
                 extra={<p className={'mt-3 leading-1'}>Una breve descrizione della tua attività, il tuo motto o i tuoi
                   prodotti</p>}>
        <Input.TextArea
          rows={2}
          autoSize
          placeholder={
            'Descrivi la tua attività, il tuo motto o i tuoi prodotti'
          }
          maxLength={1000}
        />
      </Form.Item>
      <div
        className={'px-2 py-5 border-t'}
      >
        <Form.Item
          rules={[{ required: true }]}
          name="points_ratio"
          label={'Rapporto euro/punti'}
          extra={
            <div><p className={'mt-3 leading-1 mb-1'}>Esempi conversione:</p>
              <div className={'flex justify-between gap-3 flex-wrap text-black'}>
                <p>
                  10€ <ArrowRightOutlined />{' '}
                  {shopProfile?.data?.points_ratio ? Math.round(shopProfile?.data?.points_ratio * 10) : 0} punti
                </p>
                <p>
                  25€ <ArrowRightOutlined />{' '}
                  {shopProfile?.data?.points_ratio ? Math.round(shopProfile?.data?.points_ratio * 25) : 0} punti
                </p>
                <p>
                  50€ <ArrowRightOutlined />{' '}
                  {shopProfile?.data?.points_ratio ? Math.round(shopProfile?.data?.points_ratio * 50) : 0} punti
                </p>
              </div>
            </div>}
        >
          <InputNumber
            addonBefore={'Un euro equivale a'}
            addonAfter={'Punti'}
            min={0.1}
            name={'relation'}
            placeholder={'Rapporto euro'}
            className={'w-full'}
          />
        </Form.Item>
      </div>
      <Form.Item
        className={'px-2 py-5 border-t'}
        label={'Punti iniziali'}
        name="initial_points"
        extra={<p className={'mt-2'}>Scegli quanti punti assegnare ad un cliente quando aggiunge il tuo negozio per la
          prima volta.</p>}
        rules={[
          {
            required: true,
            message: 'Inserisci un valore tra 0 e 5000'
          }
        ]}
      >
        <InputNumber
          /* value={defaultPoints}
           onChange={(e) => e !== null && setDefaultPoints(e)}
           defaultValue={defaultPoints}*/
          style={{ width: '100%' }}
          placeholder="Punti iniziali"
        />
      </Form.Item>
      <div className={'px-2 py-5 border-t'}>
        <p className={'font-medium mb-2'}>Traguardi</p>
        {/*@ts-ignore*/}
        <Form.List required rules={[{ required: true }]} name="goals">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 15
                  }}
                  key={key}
                >
                  <Form.Item
                    noStyle
                    {...restField}
                    name={[name, 'title']}
                    rules={[
                      {
                        required: true,
                        message: 'Inserire il nome del premio'
                      }
                    ]}
                  >
                    <Input
                      autoComplete="off"
                      maxLength={30}
                      style={{ width: '100%' }}
                      placeholder="Nome del premio"
                    />
                  </Form.Item>
                  <Form.Item
                    noStyle
                    {...restField}
                    name={[name, 'number']}
                    rules={[
                      {
                        required: true,
                        message: 'Inserire il nome del premio'
                      }
                    ]}
                  >
                    <InputNumber
                      min={1}
                      max={5000}
                      style={{ width: '100%' }}
                      placeholder="Punti richiesti"
                    />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => !saveProfile.isPending && remove(name)} />
                </div>
              ))}
              <Form.Item>
                <Button
                  disabled={shopProfile.data.goals?.length > 3}
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Aggiungi obiettivo
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </div>
      <p className={'text-slate-500 px-2 mb-[40px]'}>
        Puoi inserire fino a 4 traguardi, il punteggio massimo per un traguardo
        è 5000
      </p>
      <Form.Item
        className={'px-2 py-5 border-t'}
        label={'Scadenza punti'}
        name="points_expiration_days"
        extra={<p className={'mt-2'}>Puoi impostare dopo quanto tempo dall'ultima transazione i punti dei clienti
          vengono ripristinati ai punti iniziali.</p>}
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Punti iniziali"
          options={[
            { label: 'Non scadono', value: null },
            { label: '1 mese', value: 30 },
            { label: '3 mesi', value: 90 },
            { label: '6 mesi (consigliato)', value: 180 },
            { label: '1 anno', value: 365 },
            { label: '2 anni', value: 730 }
          ]}
        />
      </Form.Item>
      <div
        className={'sticky bottom-10 md:bottom-0 w-full bg-white pb-4'}
      >
        <Button
          disabled={!touched}
          loading={saveProfile.isPending}
          onClick={() => {
            profileForm.submit();
          }}
          type={'primary'}
          size={'large'}
          style={{ width: '100%' }}
        >
          Salva le modifiche
        </Button>
      </div>
    </Form>
  );
}
